import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import Icon from './icons';
import ScreenPicker from './ScreenPicker';
import { useSch } from './context';
import OccasionPicker from './OccasionPicker';
import {
  EVERY_DAY, PRESETS, WEEKDAYS_SUN_THU, WEEKEND_FRI_SAT, onceProblem, onceTimes, onceValid, screensOfWhere,
} from '../../../schedule/model';

const bold = { b: <strong /> };

/** Is this step complete enough to go on? (the prototype's `canNext`) */
export function canNext(F, now) {
  if (F.step === 1) return !!F.show;
  if (F.step === 2) {
    if (F.kind === 'rule') return F.days.length > 0;
    if (F.kind === 'once') return onceValid(F, now);
    if (F.kind === 'event') return !!F.occ && !!F.start && !!F.end && F.end >= F.start && (F.evName || '').trim().length > 0;
    return false;
  }
  if (F.step === 3) return F.where.mode === 'all' || F.where.ids.length > 0;
  return true;
}

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * A time of day, to the minute: the browser's time field (any minute, e.g. 8:34), with the half-hour list as suggestions and, for
 * an end that may run to the end of the day, a «نهاية اليوم» chip (24:00, which a time field cannot hold). The value is minutes
 * since midnight; it is never rounded.
 */
function TimeField({ id, value, onChange, eod = false }) {
  const { t } = useSch();
  const listId = 'sch6-halfhours';
  const atEnd = value === 1440;
  return (
    <>
      <input
        className="sch6-inp"
        type="time"
        step="60"
        id={id}
        list={listId}
        dir="ltr"
        value={atEnd ? '' : hhmm(value)}
        onChange={(e) => {
          const m = /^(\d{2}):(\d{2})/.exec(e.target.value);
          if (m) onChange(Number(m[1]) * 60 + Number(m[2]));
        }}
      />
      {eod && (
        <button type="button" className="sch6-pchip sch6-eodchip" aria-pressed={atEnd} onClick={() => onChange(1440)}>{t('sch6.once_to_eod')}</button>
      )}
    </>
  );
}

/** The half-hour suggestions every time field offers (one list, shared by id). */
function HalfHours() {
  const opts = [];
  for (let m = 0; m < 1440; m += 30) opts.push(m);
  return <datalist id="sch6-halfhours">{opts.map((m) => <option key={m} value={hhmm(m)} />)}</datalist>;
}

/**
 * The 4-step add / edit sheet: what, when, where, review. `F` is the form (the prototype's state `F`), `set(patch)` merges
 * into it. Step 4 asks the backend for overlaps (`checkConflicts`) and offers "new wins" / "keep current".
 */
export default function AddSheet({ F, set, onClose, onSave, saving, checkConflicts, shows, pro, onUpgrade, minVersion, occasions, onCountry, autoName }) {
  const { t, f, groups, screens, now, year, showName, col, whereText } = useSch();
  const bodyRef = useRef(null);
  const [check, setCheck] = useState({ state: 'idle', conflicts: [] });

  // review step: ask the backend for overlaps once, when the step opens
  useEffect(() => {
    if (F.step !== 4) return undefined;
    let live = true;
    setCheck({ state: 'loading', conflicts: [] });
    Promise.resolve(checkConflicts(F)).then((r) => {
      if (!live) return;
      setCheck({ state: 'done', conflicts: r?.has_conflict ? r.conflicts : [], suggested: r?.suggested });
    }).catch(() => { if (live) setCheck({ state: 'failed', conflicts: [] }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [F.step]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const first = bodyRef.current?.closest('.sch6-sheet')?.querySelector('[aria-pressed="true"], .sch6-btn.sch6-pri');
    if (first && first.focus) first.focus({ preventScroll: true });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [F.step]);

  const title = F.edit ? t('sch6.sheet_edit') : t('sch6.sheet_add');
  const ok = canNext(F, now);
  const lastStep = F.step === 4;

  // screens of the new rule that run an app older than the first build that follows events ahead of time
  const oldScreens = useMemo(() => {
    if (!minVersion) return 0;
    const ids = new Set(screensOfWhere(F.where, groups));
    return screens.filter((s) => ids.has(s.id) && (s.version == null || s.version < minVersion)).length;
  }, [F.where, groups, screens, minVersion]);

  const pickKind = (kind) => set({ kind });
  const goNext = () => {
    // a multi-day event is a Pro feature: the same upgrade message as everywhere else, the sheet stays
    if (F.step === 2 && F.kind === 'event' && !pro && F.start !== F.end) { onUpgrade(); return; }
    set({ step: F.step + 1 });
  };
  const onceKey = onceProblem(F, now);
  const nowish = F.onow !== 'custom';

  const step1 = (
    <>
      <p className="sch6-q">{t('sch6.s1_q')}</p><p className="sch6-qsub">{t('sch6.s1_sub')}</p>
      {shows.length === 0 && <p className="sch6-hint">{t('sch6.s1_empty')}</p>}
      <div className="sch6-pick">
        {shows.map((s) => (
          <button type="button" key={s.id} className="sch6-opt" aria-pressed={F.show === s.id} onClick={() => set({ show: s.id })}>
            <span className="sch6-thumb" style={{ background: `linear-gradient(135deg,${col(s.id)}, color-mix(in srgb,${col(s.id)} 45%, var(--card)))` }} />
            <span style={{ minWidth: 0 }}><b>{s.name}</b>{s.hint ? <small>{s.hint}</small> : null}</span>
          </button>
        ))}
      </div>
    </>
  );

  const daysKey = [...F.days].sort().join(',');
  const dayChip = (id, days, label) => (
    <button type="button" className="sch6-pchip" aria-pressed={daysKey === days.join(',')} onClick={() => set({ days: [...days] })}>{label}</button>
  );
  const toggleDay = (d) => set({ days: F.days.includes(d) ? F.days.filter((x) => x !== d) : [...F.days, d] });

  const step2 = (
    <>
      <p className="sch6-q">{t('sch6.s2_q')}</p>
      <div className="sch6-modes">
        <button type="button" className="sch6-bigopt" aria-pressed={F.kind === 'once'} onClick={() => pickKind('once')}>
          <span className="sch6-ic"><Icon name="clock" /></span><b>{t('sch6.once')}</b><span>{t('sch6.once_d')}</span>
        </button>
        <button type="button" className="sch6-bigopt" aria-pressed={F.kind === 'rule'} onClick={() => pickKind('rule')}>
          <span className="sch6-ic"><Icon name="repeat" /></span><b>{t('sch6.weekly')}</b><span>{t('sch6.weekly_d')}</span>
        </button>
        <button type="button" className="sch6-bigopt" aria-pressed={F.kind === 'event'} onClick={() => pickKind('event')}>
          <span className="sch6-ic"><Icon name="cal" /></span><b>{t('sch6.event')}</b><span>{t('sch6.event_d')}</span>
        </button>
      </div>
      {F.kind === 'once' && (
        <>
          <div className="sch6-fld"><span className="sch6-lab">{t('sch6.once_when')}</span>
            <div className="sch6-chips">
              {['30', '60', 'eod', 'custom'].map((id) => (
                <button type="button" key={id} className="sch6-pchip" aria-pressed={F.onow === id} onClick={() => set({ onow: id, odate: now.date, ...onceTimes(id, now.min) })}>{t(`sch6.once_${id}`)}</button>
              ))}
            </div>
            {F.onow === 'custom' ? (
              <>
                <label className="sch6-fld"><span className="sch6-hint">{t('sch6.once_day')}</span>
                  <input className="sch6-inp" type="date" id="sch6-oDate" min={now.date} value={F.odate} onChange={(e) => set({ odate: e.target.value })} />
                </label>
                <div className="sch6-row2">
                  <label className="sch6-fld"><span className="sch6-hint">{t('sch6.from')}</span><TimeField id="sch6-oFrom" value={F.ofrom} onChange={(v) => set({ ofrom: v })} /></label>
                  <label className="sch6-fld"><span className="sch6-hint">{t('sch6.to')}</span><TimeField id="sch6-oTo" value={F.oto} onChange={(v) => set({ oto: v })} eod /></label>
                </div>
                {onceKey && <span className="sch6-hint" style={{ color: 'var(--danger)' }}>{t(onceKey === 'past' ? 'sch6.once_past' : 'sch6.once_endbefore')}</span>}
              </>
            ) : (
              <span className="sch6-hint">{F.oto === 1440 ? t('sch6.once_starts_eod', { from: f.tm(F.ofrom) }) : t('sch6.once_starts_to', { from: f.tm(F.ofrom), to: f.tm(F.oto) })}</span>
            )}
          </div>
          <label className="sch6-fld"><span className="sch6-lab">{`${t('sch6.once_name')} `}<span className="sch6-hint" style={{ fontWeight: 400 }}>{t('sch6.once_optional')}</span></span>
            <input className="sch6-inp" id="sch6-oName" value={F.oname || ''} placeholder={autoName(F)} onChange={(e) => set({ oname: e.target.value })} />
          </label>
          <p className="sch6-note"><Icon name="check" /><span>{t('sch6.once_note')}</span></p>
        </>
      )}
      {F.kind === 'rule' && (
        <>
          <div className="sch6-fld"><span className="sch6-lab">{t('sch6.days_q')}</span>
            <div className="sch6-chips">
              {dayChip('all', EVERY_DAY, t('sch6.d_all'))}
              {dayChip('work', WEEKDAYS_SUN_THU, t('sch6.d_work'))}
              {dayChip('wknd', WEEKEND_FRI_SAT, t('sch6.d_wknd'))}
            </div>
            <div className="sch6-dayp" role="group" aria-label={t('sch6.days_aria')}>
              {EVERY_DAY.map((i) => <button type="button" key={i} aria-pressed={F.days.includes(i)} onClick={() => toggleDay(i)}>{f.dayShort(i)}</button>)}
            </div>
            {!F.days.length && <span className="sch6-hint" style={{ color: 'var(--danger)' }}>{t('sch6.days_none')}</span>}
          </div>
          <div className="sch6-fld"><span className="sch6-lab">{t('sch6.time_q')}</span>
            <div className="sch6-chips">
              {PRESETS.map((p) => (
                <button type="button" key={p.id} className="sch6-pchip" aria-pressed={F.preset === p.id} onClick={() => set(p.from != null ? { preset: p.id, from: p.from, to: p.to } : { preset: p.id })}>
                  {t(`sch6.p_${p.id}`)}{p.from != null && p.id !== 'allday' ? <small>{`${f.tmShort(p.from)} – ${f.tmShort(p.to)}`}</small> : null}
                </button>
              ))}
            </div>
            {F.preset === 'custom' && (
              <>
                <div className="sch6-row2">
                  <label className="sch6-fld"><span className="sch6-hint">{t('sch6.from')}</span><TimeField id="sch6-tFrom" value={F.from} onChange={(v) => set({ from: v })} /></label>
                  <label className="sch6-fld"><span className="sch6-hint">{t('sch6.to')}</span><TimeField id="sch6-tTo" value={F.to} onChange={(v) => set({ to: v })} /></label>
                </div>
                {F.to <= F.from && F.to !== F.from && <span className="sch6-hint">{t('sch6.next_day')}</span>}
              </>
            )}
          </div>
        </>
      )}
      {F.kind === 'event' && (
        <>
          <OccasionPicker F={F} set={set} occasions={occasions} onCountry={onCountry} pro={pro} onUpgrade={onUpgrade} />
          {F.occ && (
            <>
          <label className="sch6-fld"><span className="sch6-lab">{t('sch6.ev_name')}</span>
            <input className="sch6-inp" id="sch6-evName" value={F.evName || ''} placeholder={t('sch6.ev_name_ph')} onChange={(e) => set({ evName: e.target.value })} />
          </label>
          <div className="sch6-row2">
            <label className="sch6-fld"><span className="sch6-lab">{t('sch6.ev_from')}</span>
              <input className="sch6-inp" type="date" id="sch6-evStart" value={F.start} onChange={(e) => set({ start: e.target.value, end: F.end < e.target.value ? e.target.value : F.end })} />
            </label>
            <label className="sch6-fld"><span className="sch6-lab">{t('sch6.ev_to')}</span>
              <input className="sch6-inp" type="date" id="sch6-evEnd" value={F.end} onChange={(e) => set({ end: e.target.value })} />
            </label>
          </div>
          {F.end < F.start
            ? <span className="sch6-hint" style={{ color: 'var(--danger)' }}>{t('sch6.ev_end_before')}</span>
            : <span className="sch6-hint">{f.durationTxt(F.start, F.end, year)}</span>}
          <div className="sch6-fld"><span className="sch6-lab">{t('sch6.during')}</span>
            <div className="sch6-chips">
              <button type="button" className="sch6-pchip" aria-pressed={F.allDay} onClick={() => set({ allDay: true })}>{t('sch6.ev_allday')}</button>
              <button type="button" className="sch6-pchip" aria-pressed={!F.allDay} onClick={() => set({ allDay: false })}>{t('sch6.ev_hours')}</button>
            </div>
            {!F.allDay && (
              <div className="sch6-row2">
                <label className="sch6-fld"><span className="sch6-hint">{t('sch6.from')}</span><TimeField id="sch6-eFrom" value={F.efrom} onChange={(v) => set({ efrom: v })} /></label>
                <label className="sch6-fld"><span className="sch6-hint">{t('sch6.to')}</span><TimeField id="sch6-eTo" value={F.eto} onChange={(v) => set({ eto: v })} /></label>
              </div>
            )}
          </div>
          <p className="sch6-note"><Icon name="alert" /><span>{t('sch6.ev_note')}</span></p>
            </>
          )}
        </>
      )}
    </>
  );

  const step3 = (
    <ScreenPicker
      where={F.where}
      q={F.q}
      onMode={(m) => { if (F.where.mode !== m) set({ where: { mode: m, ids: [] } }); }}
      onGroups={(ids) => set({ where: { mode: 'groups', ids } })}
      onScreens={(ids) => set({ where: { mode: 'screens', ids } })}
      onQuery={(q) => set({ q })}
    />
  );

  const showN = showName(F.show);
  const whereN = whereText({ where: F.where });
  const sentence = F.kind === 'once'
    ? (
      <Trans
        i18nKey="sch6.sentence_once"
        values={{
          show: showN,
          day: F.odate === now.date ? t('sch6.today') : t('sch6.when_one', { date: f.dtxt(F.odate, true, year) }),
          range: `${nowish ? t('sch6.once_from_now') : t('sch6.once_from', { t: f.tm(F.ofrom) })} ${F.oto === 1440 ? t('sch6.once_to_eod2') : t('sch6.once_to', { t: f.tm(F.oto) })}`,
          where: whereN,
        }}
        components={bold}
      />
    )
    : F.kind === 'rule'
    ? <Trans i18nKey="sch6.sentence_rule" values={{ show: showN, days: f.daysTxt(F.days), range: f.rangeTxt(F.from, F.to), where: whereN }} components={bold} />
    : (
      <Trans
        i18nKey="sch6.sentence_event"
        values={{
          show: showN,
          name: F.evName || '',
          when: F.start === F.end ? t('sch6.when_one', { date: f.dtxt(F.start, true, year) }) : t('sch6.range_from_to', { a: f.dtxt(F.start, true, year), b: f.dtxt(F.end, true, year) }),
          range: F.allDay ? t('sch6.all_day') : f.rangeTxt(F.efrom, F.eto),
          where: whereN,
        }}
        components={bold}
      />
    );

  const hasConflict = check.state === 'done' && check.conflicts.length > 0;
  const step4 = (
    <>
      <p className="sch6-q">{t('sch6.s4_q')}</p>
      <div className="sch6-summary">{sentence}</div>
      {check.state === 'loading' && <p className="sch6-note" role="status"><Icon name="clock" /><span>{t('sch6.checking')}</span></p>}
      {check.state === 'failed' && <p className="sch6-note"><Icon name="alert" /><span>{t('sch6.check_failed')}</span></p>}
      {hasConflict && (
        <div className="sch6-conflict">
          <div className="sch6-h"><Icon name="alert" />
            <span>{check.conflicts.length === 1
              ? f.conflictTxt(check.conflicts[0], F, screens.length, year, now.date)
              : check.conflicts.map((c) => <span key={c.rule_id} style={{ display: 'block' }}>{f.conflictTxt(c, F, screens.length, year, now.date)}</span>)}</span>
          </div>
          <label className="sch6-radio"><input type="radio" name="sch6-res" value="new" checked={F.resolve === 'new'} onChange={() => set({ resolve: 'new' })} /> <span>{t('sch6.res_new', { show: showN })}</span></label>
          <label className="sch6-radio"><input type="radio" name="sch6-res" value="old" checked={F.resolve === 'old'} onChange={() => set({ resolve: 'old' })} /> <span>{t('sch6.res_old')}</span></label>
        </div>
      )}
      {check.state === 'done' && !hasConflict && <p className="sch6-note" style={{ color: 'var(--ok)' }}><Icon name="check" /><span>{t('sch6.no_conflict')}</span></p>}
      {oldScreens > 0 && (F.kind === 'event' || F.kind === 'once') && <p className="sch6-note sch6-warnnote"><Icon name="alert" /><span>{t('sch6.old_ver', { n: f.num(oldScreens) })}</span></p>}
    </>
  );

  return (
    <div className="sch6-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sch6-sheet" role="dialog" aria-modal="true" aria-labelledby="sch6-shT">
        <div className="sch6-sh-top">
          <h3 id="sch6-shT">{`${title} `}<span className="sch6-meta">· {t('sch6.step_of', { n: f.num(F.step), of: f.num(4) })}</span></h3>
          <button type="button" className="sch6-icon-btn" onClick={onClose} aria-label={t('sch6.close')}><Icon name="x" /></button>
        </div>
        <div className="sch6-steps" aria-hidden="true">{[1, 2, 3, 4].map((i) => <i key={i} className={i <= F.step ? 'sch6-on' : ''} />)}</div>
        <div className="sch6-sh-body" ref={bodyRef}>
          {F.step === 1 && step1}
          {F.step === 2 && step2}
          {F.step === 2 && <HalfHours />}
          {F.step === 3 && step3}
          {F.step === 4 && step4}
        </div>
        <div className="sch6-sh-foot">
          <button type="button" className="sch6-btn sch6-ghost" onClick={F.step > 1 ? () => set({ step: F.step - 1 }) : onClose}>{F.step > 1 ? t('sch6.back') : t('sch6.cancel')}</button>
          <button
            type="button"
            className="sch6-btn sch6-pri"
            disabled={!ok || saving || (lastStep && check.state === 'loading')}
            onClick={lastStep ? () => onSave(hasConflict ? F.resolve : undefined, check) : goNext}
          >{lastStep ? (saving ? t('sch6.saving') : (F.edit ? t('sch6.save_edit') : t('sch6.save'))) : t('sch6.next_btn')}</button>
        </div>
      </div>
    </div>
  );
}
